import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ClipboardList, Clock, Eye, EyeOff } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EstimateFileField } from "@/components/EstimateFileField";

export const ACQUISITIONS = [
  ["trade_in", "Local trade-in"],
  ["lease_return", "Lease return"],
  ["auction", "Purchased at auction"],
  ["dealer_purchase", "Dealer purchase"],
  ["rental_fleet", "Former rental / fleet vehicle"],
  ["other", "Other"],
];
export const insiderCount = (v) => [v?.recon_status || v?.recon_file, v?.acquisition].filter(Boolean).length;

const NONE = "__none__";
const textCls = "bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100 min-h-[64px]";
const selCls = "bg-slate-950 border-slate-800 text-slate-100";
const daysOnLot = (v) => {
  const iso = v?.arrived_at || v?.created_at;
  return iso ? Math.max(Math.floor((Date.now() - new Date(iso).getTime()) / 86400000), 0) : null;
};

// Per-unit insider details a shopper can unlock on the QR page: recon report, time on lot, how the unit was acquired.
export default function InsiderDialog({ vehicle, walls, open, onOpenChange, onSaved }) {
  const [recon, setRecon] = useState(NONE);
  const [reconNotes, setReconNotes] = useState("");
  const [acq, setAcq] = useState(NONE);
  const [acqNotes, setAcqNotes] = useState("");
  const [showLot, setShowLot] = useState(true);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(vehicle);

  useEffect(() => {
    if (!vehicle) return;
    setCurrent(vehicle);
    setRecon(vehicle.recon_status || NONE);
    setReconNotes(vehicle.recon_notes || "");
    setAcq(vehicle.acquisition || NONE);
    setAcqNotes(vehicle.acquisition_notes || "");
    setShowLot(vehicle.show_time_on_lot !== false);
  }, [vehicle, open]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/vehicles/${vehicle.id}/insider`, {
        recon_status: recon === NONE ? null : recon,
        recon_notes: reconNotes,
        acquisition: acq === NONE ? null : acq,
        acquisition_notes: acqNotes,
        show_time_on_lot: showLot,
      });
      onSaved?.(data);
      toast.success("Insider details saved");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!vehicle) return null;
  const days = daysOnLot(vehicle);
  const off = (k) => walls && !walls[k];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="insider-dialog" className="bg-[#0F111A] border-[#2A2E3D] text-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-extrabold flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-[#7FC3FF]" /> Insider details
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {vehicle.year} {vehicle.make} {vehicle.model} · Stock {vehicle.stock_number || "—"} · Shoppers unlock these on the QR page by leaving their contact info.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-3" data-testid="insider-recon-section">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-slate-200 font-semibold">Reconditioning report</Label>
              {off("recon") && <span className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-400" data-testid="insider-recon-off">wall off in Settings</span>}
            </div>
            <Select value={recon} onValueChange={setRecon}>
              <SelectTrigger data-testid="insider-recon-status" className={selCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                <SelectItem value={NONE}>Not offered on this unit</SelectItem>
                <SelectItem value="requested">Requested — shop is putting it together</SelectItem>
                <SelectItem value="completed">Completed — report ready</SelectItem>
              </SelectContent>
            </Select>
            <Textarea data-testid="insider-recon-notes" value={reconNotes} onChange={(e) => setReconNotes(e.target.value)} placeholder="What was done — e.g. new tires, brake pads & rotors, full detail, 150-point inspection passed…" className={textCls} disabled={recon === NONE} />
            <EstimateFileField kind="recon" vehicle={current} disabled={recon === NONE} onChanged={(u) => { setCurrent(u); onSaved?.(u); }} />
          </section>

          <section className="rounded-lg border border-slate-800 bg-slate-950/60 p-4 space-y-3" data-testid="insider-acquisition-section">
            <div className="flex items-center justify-between gap-3">
              <Label className="text-slate-200 font-semibold">How we acquired it</Label>
              {off("acquisition") && <span className="text-[10px] font-mono-tech uppercase tracking-widest text-amber-400" data-testid="insider-acquisition-off">wall off in Settings</span>}
            </div>
            <Select value={acq} onValueChange={setAcq}>
              <SelectTrigger data-testid="insider-acquisition" className={selCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#12141C] border-[#2A2E3D] text-slate-200">
                <SelectItem value={NONE}>Not offered on this unit</SelectItem>
                {ACQUISITIONS.map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="insider-acquisition-notes" value={acqNotes} onChange={(e) => setAcqNotes(e.target.value)} placeholder="Shopper-facing note — e.g. one-owner local trade, serviced here since new." className={textCls} disabled={acq === NONE} />
          </section>

          <section className="flex items-center justify-between gap-4 rounded-lg border border-slate-800 bg-slate-950/60 px-4 py-3" data-testid="insider-lot-section">
            <div className="flex items-center gap-2 text-sm">
              {showLot ? <Eye className="h-4 w-4 text-[#7FC3FF]" /> : <EyeOff className="h-4 w-4 text-slate-500" />}
              <div>
                <div className="font-medium flex items-center gap-1.5"><Clock className="h-3.5 w-3.5 text-slate-400" /> Time on lot{days != null ? <span className="text-slate-400 font-normal" data-testid="insider-lot-days"> · {days === 0 ? "arrived today" : `${days} day${days === 1 ? "" : "s"}`}</span> : null}</div>
                <div className="text-[11px] text-slate-500">
                  {vehicle.lot_date ? `From your website's lot date (${vehicle.lot_date}).` : "From the day Vinconex first saw this unit."}
                  {off("time_on_lot") ? <span className="text-amber-400"> Wall is off in Settings.</span> : " Turn off to hide it for this unit."}
                </div>
              </div>
            </div>
            <Switch data-testid="insider-lot-switch" checked={showLot} onCheckedChange={setShowLot} />
          </section>

          <Button onClick={save} disabled={busy} data-testid="insider-save-btn" className="w-full h-10 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
